import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Loader2, Store, Plane } from 'lucide-react';

// Import dashboard components
import DashboardSidebar, { MobileMenuButton } from '../components/dashboard/DashboardSidebar';
import { ClientHomeDashboard, CrafterHomeDashboard, DashboardSettings } from '../components/dashboard';

// Import the actual dashboard contents (we'll render specific sections)
import ClientDashboardContent from './ClientDashboardContent';
import CrafterDashboardContent from './CrafterDashboardContent';
import ProfilePage from './ProfilePage';
import MessagesPage from './MessagesPage';
import FavoritesPage from './FavoritesPage';
import AssistanceCasesList from '../components/AssistanceCasesList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wrapper components to embed pages without their own layout
const MessagesPageContent = () => <MessagesPage embedded={true} />;
const FavoritesPageContent = () => <FavoritesPage embedded={true} />;

export default function UnifiedDashboard() {
  const { user, profile, loading: authLoading, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get params from URL
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab');
  const sectionFromUrl = searchParams.get('section');
  const requestIdFromUrl = searchParams.get('requestId');
  
  // Role state: 'client' or 'crafter'
  const [activeRole, setActiveRole] = useState('client');
  // Section state for sidebar navigation
  const [activeSection, setActiveSection] = useState('home');
  // Orders filter state (for navigating from panoramica to specific tab)
  const [ordersFilter, setOrdersFilter] = useState(null); // null | 'active' | 'completed' | 'in_progress'
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Data for home dashboards
  const [clientOrders, setClientOrders] = useState([]);
  const [clientRequests, setClientRequests] = useState([]);
  const [crafterOrders, setCrafterOrders] = useState([]);
  const [crafterRequests, setCrafterRequests] = useState([]);
  const [crafterDirectRequests, setCrafterDirectRequests] = useState([]);
  const [crafterProposals, setCrafterProposals] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  
  // Stripe Connect state for crafter home
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [pendingBalance, setPendingBalance] = useState({ amount: 0, ordersCount: 0 });
  
  // Badge counts
  const [badges, setBadges] = useState({});

  // Stato account artigiano (vetrina disattivata / ferie) per banner persistente
  const [crafterAccountStatus, setCrafterAccountStatus] = useState(null);
  
  // Ref to prevent reload on browser tab switch
  const dataLoadedRef = useRef(false);
  const userIdRef = useRef(null);

  // Determine if user can access crafter mode
  const isCrafterEnabled = profile?.role === 'crafter' || profile?.is_crafter_enabled;

  // Carica stato account artigiano (vetrina / ferie) per il banner persistente
  useEffect(() => {
    if (!user || !isCrafterEnabled) { setCrafterAccountStatus(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const res = await fetch(`${API_URL}/api/account/status`, {
          headers: { Authorization: `Bearer ${data?.session?.access_token}` },
        });
        if (res.ok && !cancelled) setCrafterAccountStatus(await res.json());
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [user, isCrafterEnabled, activeSection]);

  // Initialize from URL params
  useEffect(() => {
    if (requestIdFromUrl) {
      setActiveRole('client');
      setActiveSection('open-requests');
    } else if (tabFromUrl === 'cliente' || tabFromUrl === 'client') {
      setActiveRole('client');
      if (sectionFromUrl) setActiveSection(sectionFromUrl);
      else setActiveSection('home');
    } else if (tabFromUrl === 'artigiano' || tabFromUrl === 'crafter') {
      setActiveRole('crafter');
      if (sectionFromUrl) setActiveSection(sectionFromUrl);
      else setActiveSection('home');
    } else if (tabFromUrl === 'ordini') {
      // Old URL format support
      setActiveRole('client');
      setActiveSection('active-orders');
    } else if (tabFromUrl === 'richieste') {
      // Old URL format support
      setActiveRole('client');
      setActiveSection('open-requests');
    } else if (tabFromUrl === 'orders') {
      // Support ?tab=orders for crafter
      if (profile?.role === 'crafter' || profile?.is_crafter_enabled) {
        setActiveRole('crafter');
        setActiveSection('orders');
      } else {
        setActiveRole('client');
        setActiveSection('active-orders');
      }
    } else if (profile?.role === 'crafter') {
      setActiveRole('crafter');
    }
  }, [tabFromUrl, sectionFromUrl, requestIdFromUrl, profile?.role, profile?.is_crafter_enabled]);

  // Track if crafter data needs to be loaded
  const crafterDataLoadedRef = useRef(false);

  // Load data for dashboards
  useEffect(() => {
    // Only load data if user and profile exist
    if (user && profile) {
      // Reset crafter data loaded flag when profile changes
      if (userIdRef.current !== user.id) {
        crafterDataLoadedRef.current = false;
        dataLoadedRef.current = false;
      }
      
      // Load initial client data if not loaded yet
      if (!dataLoadedRef.current) {
        userIdRef.current = user.id;
        dataLoadedRef.current = true;
        loadDashboardData();
      } else if (isCrafterEnabled && !crafterDataLoadedRef.current) {
        // Profile just changed to crafter - need to load crafter data
        crafterDataLoadedRef.current = true;
        loadDashboardData();
      }
    }
  }, [user, profile, isCrafterEnabled]);

  const loadDashboardData = async () => {
    setDataLoading(true);
    try {
      // Variables to track crafter data for badge calculation
      let loadedCrafterOrders = [];
      let loadedDirectRequests = [];

      // Load client orders
      const { data: ordersData } = await supabase
        .from('orders')
        .select('*, crafter:crafter_id(id, full_name, avatar_url)')
        .eq('client_id', user.id)
        .order('created_at', { ascending: false });
      setClientOrders(ordersData || []);

      // Load client requests
      const { data: requestsData } = await supabase
        .from('direct_requests')
        .select('*, proposals(count)')
        .eq('client_id', user.id)
        .order('created_at', { ascending: false });
      setClientRequests(requestsData || []);

      // If user is crafter, load crafter data
      if (isCrafterEnabled) {
        // Crafter orders
        const { data: crafterOrdersData } = await supabase
          .from('orders')
          .select('*, client:client_id(id, full_name)')
          .eq('crafter_id', user.id)
          .order('created_at', { ascending: false });
        loadedCrafterOrders = crafterOrdersData || [];
        setCrafterOrders(loadedCrafterOrders);

        // Public requests matching crafter categories
        if (profile?.categories?.length > 0) {
          const { data: matchingRequests } = await supabase
            .from('direct_requests')
            .select('*')
            .in('category', profile.categories)
            .eq('status', 'open')
            .is('crafter_id', null)
            .order('created_at', { ascending: false });
          setCrafterRequests(matchingRequests || []);
        }

        // Direct requests to this crafter
        const { data: directReqs } = await supabase
          .from('direct_requests')
          .select('*')
          .eq('crafter_id', user.id)
          .order('created_at', { ascending: false });
        loadedDirectRequests = directReqs || [];
        setCrafterDirectRequests(loadedDirectRequests);

        // Crafter proposals
        const { data: proposalsData } = await supabase
          .from('proposals')
          .select('*, direct_requests(title, status)')
          .eq('crafter_id', user.id)
          .order('created_at', { ascending: false });
        setCrafterProposals(proposalsData || []);
      }

      // Calculate badges
      const newBadges = {
        'active-orders': (ordersData || []).filter(o => ['confirmed', 'in_progress', 'ready', 'shipped'].includes(o.status)).length,
        'open-requests': (requestsData || []).filter(r => r.status === 'open').length,
        'orders': isCrafterEnabled ? loadedCrafterOrders.filter(o => ['confirmed', 'in_progress', 'ready', 'shipped'].includes(o.status)).length : 0,
        'client-requests': isCrafterEnabled ? (loadedDirectRequests.filter(r => r.status === 'open').length) : 0,
      };
      setBadges(newBadges);
      
      // Check Stripe status for crafters
      if (isCrafterEnabled) {
        try {
          setStripeLoading(true);
          const stripeResp = await fetch(`${API_URL}/api/connect/account-status/${user.id}`);
          if (stripeResp.ok) {
            const stripeData = await stripeResp.json();
            setStripeConnected(stripeData.has_account && stripeData.charges_enabled);
          }
          
          // Calculate pending balance from completed orders
          const { data: completedOrders } = await supabase
            .from('orders')
            .select('total_price')
            .eq('crafter_id', user.id)
            .in('status', ['completed', 'shipped', 'delivered']);
          
          if (completedOrders && completedOrders.length > 0) {
            const totalAmount = completedOrders.reduce((sum, o) => sum + (parseFloat(o.total_price) || 0), 0);
            setPendingBalance({
              amount: totalAmount,
              ordersCount: completedOrders.length
            });
          }
        } catch (err) {
          console.error('Error checking Stripe status:', err);
        } finally {
          setStripeLoading(false);
        }
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setDataLoading(false);
    }
  };

  // Handle role switch
  const handleRoleSwitch = (newRole) => {
    if (newRole === 'crafter' && !isCrafterEnabled) {
      // Show become crafter page
      setActiveRole('crafter');
      setActiveSection('become-crafter');
    } else {
      setActiveRole(newRole);
      setActiveSection('home');
    }
    // Update URL
    navigate(`?tab=${newRole === 'crafter' ? 'artigiano' : 'cliente'}`, { replace: true });
  };

  // Handle section change (with optional filter for sub-tabs like orders/completed)
  const handleSectionChange = (section, filter = null) => {
    setActiveSection(section);
    setOrdersFilter(filter);
    // Update URL with section
    navigate(`?tab=${activeRole === 'crafter' ? 'artigiano' : 'cliente'}&section=${section}`, { replace: true });
  };

  // Navigate to plans page
  const handleBecomeCrafter = () => {
    navigate('/plans');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-[#008080]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Render the appropriate content based on role and section
  const renderContent = () => {
    // Client sections
    if (activeRole === 'client') {
      switch (activeSection) {
        case 'home':
          return (
            <ClientHomeDashboard 
              orders={clientOrders}
              requests={clientRequests}
              onNavigate={handleSectionChange}
            />
          );
        case 'active-orders':
        case 'completed-orders':
          return (
            <ClientDashboardContent 
              key={`orders-${activeSection}`}
              viewMode="orders"
              ordersFilter={activeSection === 'active-orders' ? 'active' : 'completed'}
              autoOpenRequestId={null}
            />
          );
        case 'open-requests':
        case 'inprogress-requests':
          return (
            <ClientDashboardContent 
              key={`requests-${activeSection}`}
              viewMode="requests"
              requestsFilter={activeSection === 'open-requests' ? 'open' : 'inprogress'}
              autoOpenRequestId={requestIdFromUrl}
            />
          );
        case 'assistance':
          return <AssistanceCasesList role="buyer" />;
        case 'messages':
          return <MessagesPageContent />;
        case 'favorites':
          return <FavoritesPageContent />;
        case 'settings':
          return <DashboardSettings role="client" />;
        default:
          return (
            <ClientHomeDashboard 
              orders={clientOrders}
              requests={clientRequests}
              onNavigate={handleSectionChange}
            />
          );
      }
    }

    // Crafter sections
    if (activeRole === 'crafter') {
      // Show become crafter page if not enabled
      if (!isCrafterEnabled || activeSection === 'become-crafter') {
        return (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="p-12 text-center bg-gradient-to-br from-[#DAA520]/5 to-[#DAA520]/10 border-2 border-dashed border-[#DAA520]/30">
              <div className="max-w-md mx-auto">
                <motion.div
                  animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="inline-block mb-6"
                >
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#DAA520] to-[#B8860B] flex items-center justify-center shadow-lg">
                    <Sparkles className="w-12 h-12 text-white" />
                  </div>
                </motion.div>
                
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                  Vuoi iniziare a vendere le tue creazioni?
                </h2>
                <p className="text-slate-600 mb-8">
                  Apri la tua vetrina su Rarifex e raggiungi clienti in cerca di pezzi unici e artigianali.
                </p>
                
                <Button
                  onClick={handleBecomeCrafter}
                  className="bg-[#DAA520] text-white hover:bg-[#B8860B] px-8 py-6 text-lg font-semibold shadow-lg"
                  data-testid="activate-crafter-btn"
                >
                  Scopri i Piani
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>

                <div className="mt-10 grid grid-cols-3 gap-4 text-center">
                  <div className="p-4">
                    <div className="text-2xl font-bold text-[#DAA520]">8%</div>
                    <div className="text-xs text-slate-500">Fee solo sulle vendite</div>
                  </div>
                  <div className="p-4">
                    <div className="text-2xl font-bold text-[#DAA520]">∞</div>
                    <div className="text-xs text-slate-500">Prodotti illimitati</div>
                  </div>
                  <div className="p-4">
                    <div className="text-2xl font-bold text-[#DAA520]">0€</div>
                    <div className="text-xs text-slate-500">Per iniziare</div>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        );
      }

      switch (activeSection) {
        case 'home':
          return (
            <CrafterHomeDashboard 
              orders={crafterOrders}
              requests={crafterRequests}
              directRequests={crafterDirectRequests}
              proposals={crafterProposals}
              conversations={conversations}
              stripeConnected={stripeConnected}
              stripeLoading={stripeLoading}
              pendingBalance={pendingBalance}
              onNavigate={handleSectionChange}
            />
          );
        case 'orders':
          return <CrafterDashboardContent key={`crafter-orders-${ordersFilter}`} viewMode="orders" initialOrdersView={ordersFilter || 'active'} />;
        case 'assistance':
          return <AssistanceCasesList role="seller" />;
        case 'shipping':
          return <CrafterDashboardContent key="crafter-shipping" viewMode="shipping" />;
        case 'stripe-setup':
          return <CrafterDashboardContent key="crafter-stripe-setup" viewMode="stripe-setup" />;
        case 'payouts':
          return <CrafterDashboardContent key="crafter-payouts" viewMode="payouts" />;
        case 'products':
          return <CrafterDashboardContent key="crafter-products" viewMode="products" />;
        case 'inventory':
          return <CrafterDashboardContent key="crafter-inventory" viewMode="inventory" />;
        case 'client-requests':
          return <CrafterDashboardContent key="crafter-requests" viewMode="requests" />;
        case 'proposals':
          return <CrafterDashboardContent key="crafter-proposals" viewMode="proposals" />;
        case 'fans':
          return <CrafterDashboardContent key="crafter-fans" viewMode="fans" />;
        case 'storefront':
          return <CrafterDashboardContent key="crafter-storefront" viewMode="storefront" />;
        case 'account':
          return <ProfilePage />;
        case 'feedback':
          return <CrafterDashboardContent key="crafter-feedback" viewMode="feedback" />;
        case 'settings':
          return <DashboardSettings role="crafter" />;
        default:
          return (
            <CrafterHomeDashboard 
              orders={crafterOrders}
              requests={crafterRequests}
              directRequests={crafterDirectRequests}
              proposals={crafterProposals}
              stripeConnected={stripeConnected}
              stripeLoading={stripeLoading}
              pendingBalance={pendingBalance}
              onNavigate={handleSectionChange}
            />
          );
      }
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="unified-dashboard">
      {/* Sidebar */}
      <DashboardSidebar
        role={activeRole}
        activeSection={activeSection}
        activeFilter={ordersFilter}
        onSectionChange={handleSectionChange}
        onRoleSwitch={handleRoleSwitch}
        isCrafterEnabled={isCrafterEnabled}
        badges={badges}
        userName={profile?.full_name}
        mobileMenuOpen={mobileMenuOpen}
        onMobileMenuOpenChange={setMobileMenuOpen}
      />

      {/* Main Content Area */}
      <main className="flex-1 min-h-screen">
        {/* Mobile Menu Button - sticky at top of content area */}
        <MobileMenuButton onOpen={() => setMobileMenuOpen(true)} />

        {/* Banner persistente stato vetrina / ferie (solo area artigiano) */}
        {activeRole === 'crafter' && crafterAccountStatus?.storefront_disabled && (
          <div className="max-w-5xl mx-auto px-4 md:px-8 pt-4" data-testid="storefront-disabled-banner">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <Store className="w-5 h-5 text-red-600 shrink-0" />
              <p className="text-sm text-red-800 flex-1">
                <strong>La tua vetrina è disattivata.</strong> Profilo, prodotti e recensioni non sono visibili al pubblico.
              </p>
              <Button
                size="sm"
                onClick={() => handleSectionChange('settings')}
                className="bg-red-600 hover:bg-red-700 text-white shrink-0"
                data-testid="storefront-disabled-banner-cta"
              >
                Riattiva vetrina
              </Button>
            </div>
          </div>
        )}
        {activeRole === 'crafter' && !crafterAccountStatus?.storefront_disabled && crafterAccountStatus?.vacation_active && (
          <div className="max-w-5xl mx-auto px-4 md:px-8 pt-4" data-testid="vacation-banner">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <Plane className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm text-amber-800 flex-1">
                <strong>Sei in chiusura per ferie.</strong> I tuoi prodotti restano visibili ma non sono acquistabili fino al rientro.
              </p>
              <Button
                size="sm"
                onClick={() => handleSectionChange('settings')}
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                data-testid="vacation-banner-cta"
              >
                Gestisci ferie
              </Button>
            </div>
          </div>
        )}

        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
          {/* Content */}
          {dataLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-[#008080]" />
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </main>
    </div>
  );
}
